import { Injectable, inject } from '@angular/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { combineLatest } from 'rxjs';

import { LogFactory } from '../../../../common/services/logger/log-factory';
import { AudioSessionService } from '../../../core/audio-session/audio-session.service';
import { NativeAudioMixer } from '../../../core/native-audio/native-audio-mixer.service';
import { AppVisibilityService } from '../../../core/visibility/app-visibility.service';
import { WorkoutLockScreenCopy } from '../enums/workout-player.enum';
import { DbService } from './db.service';
import { PLAN_REMINDER_ID_RANGE } from './plan-reminder.service';
import { WorkoutService, WorkoutViewModel } from './workout.service';

/**
 * Seconds of silence a background task carries the app through. iOS grants
 * about thirty for the whole silent stretch, however many steps it spans; a
 * margin keeps a step boundary inside it.
 */
const HOLD_SECONDS = 30;
const HOLD_BUDGET_MS = 25_000;
/**
 * The rest-end notification's id: one at a time, replaced as the workout
 * moves on. Outside the plan reminders' range, which cancels only its own.
 */
export const STEP_END_NOTIFICATION_ID = PLAN_REMINDER_ID_RANGE.max + 1_000_001;
const PERMISSION_GRANTED = 'granted';

/**
 * A workout on a locked iPhone going quiet (ADR-0284, decision "quiet mode").
 *
 * iOS suspends an audio app about ten seconds after its sound stops, and a
 * suspended app's countdown stands still. So while the app is hidden, the
 * workout runs and nothing plays - a rest with no music, a timed exercise with
 * spoken cues only, a video exercise (its video stops in the background):
 *
 * - a background task carries the app through the next ~25 s, so a short gap
 *   ends on time and the workout moves on by itself;
 * - when the step has longer than that to go, a local notification is set for
 *   its end ("Rest over - Squats is next"), because the app will be asleep
 *   then. Unlocking catches the workout up.
 *
 * Nothing is played to keep the app awake: a silent loop is what App Review
 * rejects, and it would sit on the lock screen as the thing playing. The
 * notification only goes out when notifications are already allowed; this
 * never asks. Both end the moment sound plays again, the app is opened, or
 * the workout pauses or stops. iPhone only: Android's foreground service keeps
 * the countdown running, and the web has no lock screen.
 */
@Injectable({ providedIn: 'root' })
export class SilentStretchService {
  private readonly workout = inject(WorkoutService);
  private readonly mixer = inject(NativeAudioMixer);
  private readonly audioSession = inject(AudioSessionService);
  private readonly visibility = inject(AppVisibilityService);
  private readonly db = inject(DbService);
  private readonly logger = inject(LogFactory).createLogger(SilentStretchService);

  private started = false;
  private holding = false;
  /** When the hold began: it covers the silence from then on, not each step afresh. */
  private holdStartedAt = 0;
  /** The step and unit the notification was set for, or null when none is pending. */
  private scheduledFor: string | null = null;
  /** Bumped whenever the notification is replaced or cancelled, so a late schedule is dropped. */
  private generation = 0;

  /** Starts watching; called once at start-up. Does nothing off an iPhone. */
  start(): void {
    if (this.started || !this.mixer.enabled) return;
    this.started = true;
    combineLatest([this.visibility.visible$, this.workout.workout$, this.mixer.anyPlaying$]).subscribe(
      ([visible, vm, anyPlaying]) => this.evaluate(visible, vm, anyPlaying),
    );
  }

  private evaluate(visible: boolean, vm: WorkoutViewModel | null, anyPlaying: boolean): void {
    const silent = !visible && !!vm && !vm.isPaused && !anyPlaying;
    if (!silent || !vm) {
      this.end();
      return;
    }
    if (!this.holding) {
      this.holding = true;
      this.holdStartedAt = Date.now();
      this.audioSession.holdAwake(HOLD_SECONDS);
    }
    const unit = `${vm.activityHistory?.length ?? 0}|${vm.activityId}|${vm.isRest ? 'rest' : 'set'}|${vm.setIndex}|${vm.repIndex}`;
    if (unit === this.scheduledFor) return;
    // What is left of the hold. A run of short quiet steps (a timer-only
    // workout) uses it up part-way, and the step that outlasts it needs the
    // notification, though it is short itself.
    const covered = HOLD_BUDGET_MS - (Date.now() - this.holdStartedAt);
    if (vm.remainingMs <= covered) {
      // The background task covers it; a notification would only arrive late.
      if (this.scheduledFor !== null) this.cancelNotification();
      return;
    }
    this.scheduledFor = unit;
    void this.scheduleNotification(vm);
  }

  private end(): void {
    if (this.holding) {
      this.holding = false;
      this.audioSession.releaseHold();
    }
    if (this.scheduledFor !== null) this.cancelNotification();
  }

  private async scheduleNotification(vm: WorkoutViewModel): Promise<void> {
    const generation = ++this.generation;
    const at = new Date(Date.now() + vm.remainingMs);
    try {
      const { display } = await LocalNotifications.checkPermissions();
      if (display !== PERMISSION_GRANTED || generation !== this.generation) return;
      const next = vm.activityQueue?.[0];
      const lastUnit = vm.setIndex === vm.setCount && vm.repIndex === vm.repCount;
      const nextName = lastUnit || vm.isRest
        ? next && (await this.db.getActivity(next.activityId))?.name
        : undefined;
      if (generation !== this.generation) return;
      // A rest's label is "Rest": "Rest - Time to move on".
      const title = vm.isRest ? `${vm.label} - ${WorkoutLockScreenCopy.STEP_OVER}` : vm.label;
      const body = nextName
        ? `${WorkoutLockScreenCopy.NEXT_UP} ${nextName}`
        : lastUnit && !next && !vm.isRest
          ? WorkoutLockScreenCopy.WORKOUT_DONE
          : WorkoutLockScreenCopy.STEP_OVER;
      await LocalNotifications.schedule({
        notifications: [{ id: STEP_END_NOTIFICATION_ID, title, body, schedule: { at, allowWhileIdle: true } }],
      });
    } catch (err) {
      this.logger.debug('Step-end notification unavailable', { reason: (err as Error)?.message });
    }
  }

  private cancelNotification(): void {
    this.scheduledFor = null;
    this.generation += 1;
    LocalNotifications.cancel({ notifications: [{ id: STEP_END_NOTIFICATION_ID }] }).catch(() => undefined);
  }
}
